import { useCallback, FC, useMemo, useState, useEffect } from 'react';
import { Menu } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { deviceQueries } from '@/config/theme';
import styles from './styles.module.scss';
import { CustomMenuItem, menuItems } from '@/config/menuItems';
import { useAuth } from '@/context/AuthContext';
import { AccountType, UserRole } from '@/types';

type Props = {
  onSelect: (value: boolean) => void;
  isOpen: boolean;
}

const defaultOpenKeys: string[] = ['cc', 'cc-accounting'];

const SiderMenu: FC<Props> = ({ onSelect, isOpen }) => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isSmallDevice = useMediaQuery({ query: deviceQueries.smallDevice });
  const [openKeys, setOpenKeys] = useState<string[]>(defaultOpenKeys);

  // When sidebar opens, ensure Command Center and Accounting stay expanded
  useEffect(() => {
    if (isOpen) {
      setOpenKeys(prevKeys => {
        const merged = new Set([...defaultOpenKeys, ...prevKeys]);
        return Array.from(merged);
      });
    }
  }, [isOpen]);

  const filterMenuItems = useCallback((items: CustomMenuItem[]): CustomMenuItem[] => {
    return items
      .filter((item) => {
        // Check if the item has allowedRoles and if the user's role is included
        let allowed = true;
        if (item.allowedRoles) {
          allowed = item.allowedRoles?.includes(auth?.user?.role as UserRole);
        }
        if (item.allowedAccountTypes) {
          allowed = item.allowedAccountTypes?.includes(auth?.user?.accountType as AccountType);
        }

        // If the item has children, filter them first
        if (item.children) {
          const filteredChildren = filterMenuItems(item.children);
          // Only include the item if it has allowed children or no allowedRoles restriction
          return allowed && filteredChildren.length > 0;
        }

        return allowed;
      })
      .map(item => {
        // Create a new object without the allowedRoles prop
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { allowedRoles, allowedAccountTypes, children, ...rest } = item;
        return {
          ...rest,
          // Recursively filter children if they exist
          ...(children && { children: filterMenuItems(children) })
        };
      });
  }, [auth]);

  const allowedMenuItems = useMemo(() => {
    return filterMenuItems(menuItems || []);
  }, [filterMenuItems]);

  /**
   * MainLayout always shows the full CC/partner menu.
   * When super admin uses "Get Temporary Access", they are logged in as END_CUSTOMER
   * and get EndCustomerLayout (different layout with Projects, Orders, etc.) - not MainLayout.
   */
  const displayMenuItems = allowedMenuItems;

  const handleClick: MenuProps['onClick'] = useCallback((info: { key: string; keyPath: string[] }) => {
    // Get the clicked key directly from info.key
    const clickedKey = info.key;

    // Find the clicked item in the menu items
    const findMenuItem = (items: CustomMenuItem[], key: string): CustomMenuItem | undefined => {
      for (const item of items) {
        if (item.key === key) return item;
        if (item.children) {
          const found = findMenuItem(item.children, key);
          if (found) return found;
        }
      }
      return undefined;
    };

    const clicked = findMenuItem(displayMenuItems, clickedKey);

    if (clicked?.path) {
      navigate(clicked.path);
    }

    if (isSmallDevice) {
      onSelect(true);
    }
  }, [navigate, isSmallDevice, onSelect, displayMenuItems]);

  const activeKey = useMemo(() => {
    // Function to find the active key by checking both top-level items and children
    const findActiveKey = (items: CustomMenuItem[]): string => {
      for (const item of items) {
        // Check if this item's path matches the current pathname
        if (item.path && pathname.includes(item.path)) {
          return item.key;
        }

        // Check children if they exist
        if (item.children) {
          const childKey = findActiveKey(item.children);
          if (childKey) {
            return childKey;
          }
        }
      }
      return '';
    };

    return findActiveKey(allowedMenuItems);
  }, [pathname, allowedMenuItems]);

  const handleOpenChange = useCallback((keys: string[]) => {
    // Always keep Accounting (cc-accounting) expanded when Command Center is shown
    const merged = new Set([...keys, ...defaultOpenKeys]);
    setOpenKeys(Array.from(merged));
  }, []);

  return (
    <div className={styles.customMenu}>
      <Menu
        mode="inline"
        selectedKeys={[activeKey]}
        openKeys={isOpen ? openKeys : []}
        onOpenChange={handleOpenChange}
        items={displayMenuItems}
        onClick={handleClick}
      />
    </div>
  );
};

export default SiderMenu;
